import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Link2, TrendingUp, TrendingDown, Globe, MessageCircle, Trash2, Download, ArrowRight, Loader2, AlertTriangle, MousePointer, Eye, Target, Monitor, Smartphone, Tablet } from "lucide-react";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";
import { exportSEOPDF, filterByDateRange, calculateChange, monthYearToRange, availableYears, previousMonthReport, monthLabel } from "../utils/reportUtils";
import { MonthYearFilter } from "../components/MonthYearFilter";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// KPI Card Component with description
const KPICard = ({ title, value, description, change, icon: Icon, color = "blue", alert = false, lowerIsBetter = false }) => {
  const colorClasses = {
    blue: { bg: "bg-blue-50", text: "text-blue-600", border: "border-blue-100" },
    green: { bg: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-100" },
    purple: { bg: "bg-purple-50", text: "text-purple-600", border: "border-purple-100" },
    amber: { bg: "bg-amber-50", text: "text-amber-600", border: "border-amber-100" },
    red: { bg: "bg-red-50", text: "text-red-600", border: "border-red-100" },
    orange: { bg: "bg-orange-50", text: "text-orange-600", border: "border-orange-100" }
  };
  
  const colors = colorClasses[color];
  const isUp = change >= 0;
  const isGood = lowerIsBetter ? change < 0 : change >= 0;

  return (
    <Card className={`stat-card ${alert ? colors.border : ''}`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-3">
          <div className={`p-3 rounded-xl ${colors.bg}`}>
            <Icon className={`w-6 h-6 ${colors.text}`} strokeWidth={1.5} />
          </div>
          {change !== undefined && change !== null && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              change === 0 ? 'bg-slate-100 text-slate-500' : isGood ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
            }`} title="Variação face ao mês anterior">
              {change !== 0 && (isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />)}
              {isUp && change !== 0 ? "+" : ""}{change.toFixed(1)}%
            </div>
          )}
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className={`text-3xl font-bold ${alert ? colors.text : 'text-slate-900'}`} style={{ fontFamily: 'Manrope' }}>
            {value}
          </p>
          <p className="text-xs text-slate-400 leading-relaxed mt-2">{description}</p>
        </div>
      </CardContent>
    </Card>
  );
};

const SEOReportsPage = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedYear, setSelectedYear] = useState("all");
  const { startDate, endDate } = monthYearToRange(selectedMonth, selectedYear);
  const years = availableYears(reports);

  useEffect(() => {
    fetchReports();
  }, []);

  useEffect(() => {
    const filtered = filterByDateRange(reports, startDate, endDate);
    setFilteredReports(filtered);
  }, [reports, startDate, endDate]);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API_URL}/reports/seo`);
      setReports(response.data);
      setFilteredReports(response.data);
    } catch (error) {
      console.error("Error fetching SEO reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    if (filteredReports.length === 0) {
      toast.error("Sem dados para exportar");
      return;
    }
    
    setExporting(true);
    try {
      const clientId = user?.client_id || filteredReports[0]?.client_id;
      if (!clientId) {
        toast.error("Cliente não identificado");
        return;
      }
      await exportSEOPDF(clientId, startDate, endDate);
      toast.success("PDF exportado com sucesso!");
    } catch (error) {
      console.error("Erro ao exportar PDF:", error);
      toast.error("Erro ao exportar PDF: " + error.message);
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setSelectedMonth("all");
    setSelectedYear("all");
  };

  const latestReport = filteredReports[0];
  const previousReport = previousMonthReport(reports, latestReport);

  const pctChange = (field) => {
    if (!latestReport || !previousReport) return null;
    if (!latestReport[field] && !previousReport[field]) return null; // sem dados nos dois meses
    return calculateChange(latestReport[field], previousReport[field]);
  };

  const chartData = filteredReports.slice(0, 6).reverse().map(r => ({
    period: r.period_start.slice(5, 10),
    backlinks: r.total_backlinks_acquired || 0,
    domains: r.new_referring_domains || 0,
    mentions: r.brand_mentions || 0,
    clicks: r.clicks || 0,
    impressions: r.impressions || 0
  }));

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i}><CardContent className="pt-6"><div className="skeleton h-32" /></CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="seo-reports-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Manrope' }}>
            <div className="p-2 bg-blue-100 rounded-xl">
              <Link2 className="w-7 h-7 text-blue-600" />
            </div>
            SEO
          </h1>
          <p className="text-slate-500 mt-2">
            Análise de backlinks, autoridade de domínio e menções da marca
          </p>
        </div>
        
        {/* Filters & Export */}
        <div className="flex flex-wrap items-end gap-3">
          <MonthYearFilter
            month={selectedMonth}
            year={selectedYear}
            onMonthChange={setSelectedMonth}
            onYearChange={setSelectedYear}
            years={years}
          />
          {(selectedMonth !== "all" || selectedYear !== "all") && (
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
              Limpar
            </Button>
          )}
          <Button 
            onClick={handleExportPDF} 
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="export-pdf-btn"
            disabled={exporting}
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            {exporting ? "A exportar..." : "Exportar PDF"}
          </Button>
        </div>
      </div>

      {/* Comparison info */}
      {latestReport && (
        <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="comparison-info">
          <ArrowRight className="w-4 h-4 text-blue-600" />
          {previousReport ? (
            <span>
              A mostrar <span className="font-medium text-slate-700">{monthLabel(latestReport.period_start)}</span> — variação face a{" "}
              <span className="font-medium text-slate-700">{monthLabel(previousReport.period_start)}</span>
            </span>
          ) : (
            <span>A mostrar {monthLabel(latestReport.period_start)} — sem mês anterior para comparar</span>
          )}
        </div>
      )}

      {latestReport && (
        <>
          {/* Section: Search Console Performance */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                Performance no Google (Search Console)
              </h2>
              <Separator className="flex-1" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <KPICard
                title="Cliques"
                value={(latestReport.clicks || 0).toLocaleString('pt-PT')}
                description="Número total de cliques orgânicos no Google Search durante o período."
                change={pctChange('clicks')}
                icon={MousePointer}
                color="blue"
              />
              <KPICard
                title="Impressões"
                value={(latestReport.impressions || 0).toLocaleString('pt-PT')}
                description="Número de vezes que o site apareceu nos resultados de pesquisa do Google."
                change={pctChange('impressions')}
                icon={Eye}
                color="green"
              />
              <KPICard
                title="CTR Médio"
                value={`${(latestReport.avg_ctr || 0).toFixed(2)}%`}
                description="Taxa média de cliques. Percentagem de impressões que resultaram em cliques."
                change={pctChange('avg_ctr')}
                icon={Target}
                color="purple"
              />
              <KPICard
                title="Posição Média"
                value={(latestReport.avg_position || 0).toFixed(1)}
                description="Posição média nos resultados de pesquisa. Quanto menor, melhor o posicionamento."
                change={pctChange('avg_position')}
                lowerIsBetter
                icon={TrendingUp}
                color="amber"
              />
            </div>
          </div>

          {/* Section: Performance por Dispositivo */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                Performance por Dispositivo
              </h2>
              <Separator className="flex-1" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <KPICard
                title="Desktop"
                value={`${(latestReport.desktop_percentage || 0).toFixed(1)}%`}
                description="Percentagem de tráfego proveniente de computadores desktop e laptops."
                change={pctChange('desktop_percentage')}
                icon={Monitor}
                color="blue"
              />
              <KPICard
                title="Mobile"
                value={`${(latestReport.mobile_percentage || 0).toFixed(1)}%`}
                description="Percentagem de tráfego proveniente de telemóveis e smartphones."
                change={pctChange('mobile_percentage')}
                icon={Smartphone}
                color="green"
              />
              <KPICard
                title="Tablet"
                value={`${(latestReport.tablet_percentage || 0).toFixed(1)}%`}
                description="Percentagem de tráfego proveniente de tablets e iPads."
                change={pctChange('tablet_percentage')}
                icon={Tablet}
                color="purple"
              />
            </div>
          </div>

          {/* Section: Link Building Performance */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                Performance de Link Building
              </h2>
              <Separator className="flex-1" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <KPICard
                title="Total Backlinks Adquiridos"
                value={(latestReport.total_backlinks_acquired || 0).toLocaleString('pt-PT')}
                description="Número total de links externos apontando para o site. Mais backlinks de qualidade melhoram o ranking."
                change={pctChange('total_backlinks_acquired')}
                icon={Link2}
                color="blue"
              />
              <KPICard
                title="Domain Authority Médio"
                value={(latestReport.avg_domain_authority || 0).toFixed(1)}
                description="Pontuação média de autoridade dos domínios que linkam. Escala 0-100, quanto maior melhor."
                change={pctChange('avg_domain_authority')}
                icon={TrendingUp}
                color="green"
              />
              <KPICard
                title="Novos Referring Domains"
                value={(latestReport.new_referring_domains || 0).toLocaleString('pt-PT')}
                description="Quantidade de novos domínios únicos que começaram a linkar para o site neste período."
                change={pctChange('new_referring_domains')}
                icon={Globe}
                color="purple"
              />
            </div>
          </div>

          {/* Section: Brand Visibility */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                Visibilidade da Marca
              </h2>
              <Separator className="flex-1" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <KPICard
                title="Menções da Marca"
                value={(latestReport.brand_mentions || 0).toLocaleString('pt-PT')}
                description="Vezes que a marca foi mencionada online, com ou sem link. Indica reconhecimento da marca."
                change={pctChange('brand_mentions')}
                icon={MessageCircle}
                color="amber"
              />
              <KPICard
                title="Links Removidos/Perdidos"
                value={(latestReport.links_removed_lost || 0).toLocaleString('pt-PT')}
                description="Backlinks que deixaram de existir. Pode indicar páginas removidas ou mudanças nos sites externos."
                change={pctChange('links_removed_lost')}
                lowerIsBetter
                icon={Trash2}
                color="red"
                alert={true}
              />
              <KPICard
                title="Links em Disavow"
                value={(latestReport.disavow_links_added || 0).toLocaleString('pt-PT')}
                description="Links tóxicos rejeitados via Google Disavow Tool. Protege contra penalizações de spam."
                change={pctChange('disavow_links_added')}
                icon={AlertTriangle}
                color="orange"
                alert={true}
              />
            </div>
          </div>
        </>
      )}

      {/* Chart */}
      <Card className="stat-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Link2 className="w-5 h-5 text-blue-600" />
            Evolução de Backlinks e Domínios
          </CardTitle>
          <CardDescription>
            Tendência mensal de aquisição de backlinks e novos domínios referenciadores
          </CardDescription>
        </CardHeader>
        <CardContent>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="period" stroke="#94A3B8" fontSize={12} />
                <YAxis stroke="#94A3B8" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px'
                  }} 
                />
                <Bar dataKey="backlinks" fill="#2563EB" name="Backlinks" radius={[4, 4, 0, 0]} />
                <Bar dataKey="domains" fill="#10B981" name="Novos Domínios" radius={[4, 4, 0, 0]} />
                <Bar dataKey="mentions" fill="#F59E0B" name="Menções" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-slate-400">
              Sem dados disponíveis
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reports Table */}
      <Card className="stat-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
              Histórico de Relatórios
            </CardTitle>
            <CardDescription>
              Todos os relatórios SEO registados
            </CardDescription>
          </div>
          <Badge variant="outline" className="badge-info">
            {filteredReports.length} relatório(s)
          </Badge>
        </CardHeader>
        <CardContent>
          {filteredReports.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Período</TableHead>
                    <TableHead className="text-right">Backlinks</TableHead>
                    <TableHead className="text-right">DA Médio</TableHead>
                    <TableHead className="text-right">Novos Domínios</TableHead>
                    <TableHead className="text-right">Menções</TableHead>
                    <TableHead className="text-right">Perdidos</TableHead>
                    <TableHead className="text-right">Disavow</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReports.map((report) => (
                    <TableRow key={report.id} data-testid={`seo-report-row-${report.id}`}>
                      <TableCell className="font-medium">
                        {report.period_start} a {report.period_end}
                      </TableCell>
                      <TableCell className="text-right">
                        {(report.total_backlinks_acquired || 0).toLocaleString('pt-PT')}
                      </TableCell>
                      <TableCell className="text-right">
                        {(report.avg_domain_authority || 0).toFixed(1)}
                      </TableCell>
                      <TableCell className="text-right">
                        {(report.new_referring_domains || 0).toLocaleString('pt-PT')}
                      </TableCell>
                      <TableCell className="text-right">
                        {(report.brand_mentions || 0).toLocaleString('pt-PT')}
                      </TableCell>
                      <TableCell className="text-right text-red-600">
                        {(report.links_removed_lost || 0).toLocaleString('pt-PT')}
                      </TableCell>
                      <TableCell className="text-right text-orange-600">
                        {(report.disavow_links_added || 0).toLocaleString('pt-PT')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Link2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Sem relatórios SEO para o período selecionado</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Key Insights */}
      {latestReport?.notes && (
        <Card className="stat-card bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
              Key Insights
            </CardTitle>
            <CardDescription>Observações e recomendações do período</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700 leading-relaxed">{latestReport.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SEOReportsPage;
